import EmptyPageContent from '@app/ccf/components/empty-page-content';
import {
  formatSortingForEndpoint,
  getSortingStateParser,
} from '@app/components/data-table/lib/parsers';
import { PageHeader } from '@app/components/layout/page/components/page-header';
import Page from '@app/components/layout/page/page';
import { reportFilters } from '@app/components/reports/report-list/reports-filters';
import ReportsTable from '@app/components/reports/report-list/reports-table';
import {
  FilterKey,
  useDataTableQueryState,
} from '@app/hooks/use-data-table-query-state';
import { soldGoodsRoutes } from '@app/sold-goods/sold-goods-routes';
import { useGetTimePeriods } from '@shared/api/hooks/reports/periods';
import {
  useCountReportRecords,
  useGetOperationReports,
} from '@shared/api/hooks/reports/reports';
import {
  CompanyReportStatusEnum,
  OperationReportTypeEnum,
  OperationReportsListParams,
} from '@shared/api/types';
import Text from '@shared/components/content/text';
import { Button } from '@shared/components/ui/button';
import { getOverallStatus } from '@shared/lib/utils';
import { parseAsString, useQueryState } from 'nuqs';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function SalesReportsListPage() {
  const navigate = useNavigate();
  const { page, perPage, ...filterStates } = useDataTableQueryState({
    filters: Object.values(reportFilters).map(
      ({ column }) => column
    ) as FilterKey[],
  });
  const [titleSearch] = useQueryState('title', parseAsString);
  const [sorting] = useQueryState('sort', getSortingStateParser());
  const [shouldPoll, setShouldPoll] = useState(true);
  const { data: periods, status: periodStatus } = useGetTimePeriods();

  // This might need to update to count sales/sales records separately
  const { mutateAsync: countRecords, status: countStatus } =
    useCountReportRecords();
  const [recordCount, setRecordCount] = useState<number>();
  useEffect(() => {
    async function fetchCount() {
      try {
        const countData = await countRecords({
          data: {
            type: OperationReportTypeEnum.sales,
          },
        });
        setRecordCount(countData.count);
      } catch (error) {
        setRecordCount(0);
      }
    }
    fetchCount();
  }, [countRecords]);

  const sortParam =
    sorting && sorting.length > 0
      ? formatSortingForEndpoint(sorting[0])
      : undefined;

  const params: OperationReportsListParams = {
    type: OperationReportTypeEnum.sales,
    page,
    page_size: perPage,
    search: titleSearch || undefined,
    sort: sortParam,
  };
  Object.values(reportFilters).forEach(({ apiKey, column }) => {
    const value = filterStates[column as keyof typeof filterStates] as
      | string[]
      | undefined;
    if (value?.length) {
      (params as Record<string, unknown>)[apiKey] = value;
    }
  });

  const { data, isFetchedAfterMount, isLoading, isPreviousData, status } =
    useGetOperationReports(params, {
      polling: shouldPoll,
      pollingInterval: 1000,
    });

  // Disable polling if there are no publishing reports
  useEffect(() => {
    if (!isFetchedAfterMount) return;

    if (data && data.results.length > 0) {
      const hasPublishingReports = data.results.some(
        (report) => report.status === CompanyReportStatusEnum.publishing
      );
      if (!hasPublishingReports) setShouldPoll(false);
    }
  }, [data, isFetchedAfterMount]);

  const hasActiveFilters =
    !!titleSearch ||
    Object.values(reportFilters).some(({ column }) => {
      const value = filterStates[column as keyof typeof filterStates] as
        | string[]
        | undefined;
      return !!value?.length;
    });

  const hasNoReports = data && data.results.length === 0 && !hasActiveFilters;
  const hasRecords = !!recordCount || !hasNoReports;
  const hasPeriods = periods && periods.length > 0;

  return (
    <Page
      name="Sales reports"
      page="page"
      status={getOverallStatus([periodStatus, countStatus])}
    >
      <PageHeader
        name="Sales reports"
        title="Sales reports"
        headingTestId="reports-list-heading"
        toolbar={
          !hasNoReports ? (
            <Button
              onClick={() => {
                navigate(soldGoodsRoutes.SALES_REPORT_CREATE);
              }}
              disabled={!hasPeriods || !hasRecords}
              data-testid="create-report-button"
            >
              New report
            </Button>
          ) : undefined
        }
        breadcrumbs={[]}
      />
      {(!hasPeriods || !hasRecords) && (
        <EmptyPageContent
          title="No sales data"
          description={
            <div className="mt-20 space-y-4">
              <Text className="text-center">
                You have not imported any sales data. Before creating reports
                you need to import your sales data.
              </Text>
            </div>
          }
          primaryAction={() => navigate(soldGoodsRoutes.SOLD_GOODS_IMPORT)}
          primaryActionText="Import data"
        />
      )}
      {hasNoReports && hasPeriods && hasRecords && (
        <EmptyPageContent
          title="Create your first sales report"
          description={
            <div className="space-y-4">
              <Text className="text-center">
                Sales Reports provide a detailed analysis of the emissions
                associated with the goods your company sells, allowing you to
                understand the total impact as well as comparing the emissions
                over time or across sites.
              </Text>
              <Text className="text-center">
                You don&rsquo;t have any sales reports yet. Once you&rsquo;ve
                started your first report you&rsquo;ll see it listed here.
              </Text>
            </div>
          }
          primaryAction={() => navigate(soldGoodsRoutes.SALES_REPORT_CREATE)}
          primaryActionText="Create sales report"
          primaryActionTestId="create-report-button"
        />
      )}
      {!hasNoReports && (
        <ReportsTable
          type="card"
          reportType={OperationReportTypeEnum.sales}
          data={data}
          status={status}
          isFetchedAfterMount={isFetchedAfterMount}
          isLoading={isLoading}
          isPreviousData={isPreviousData}
        />
      )}
    </Page>
  );
}
